/**
 * Общие типы/константы для AvailabilityEditor и ScheduleEditor — вынесены
 * отдельно, чтобы оба компонента могли на них ссылаться без циклического импорта.
 */

export const WEEKDAYS: { value: number; label: string }[] = [
  { value: 0, label: 'Пн' },
  { value: 1, label: 'Вт' },
  { value: 2, label: 'Ср' },
  { value: 3, label: 'Чт' },
  { value: 4, label: 'Пт' },
  { value: 5, label: 'Сб' },
  { value: 6, label: 'Вс' },
];

export const CANONICAL_CHANNELS: { value: string; label: string }[] = [
  { value: 'website', label: 'Сайт' },
  { value: 'tg', label: 'Telegram' },
  { value: 'vk', label: 'VK' },
];

export interface AvailabilitySchedule {
  weekdays?: number[] | null;
  time_from?: string | null;
  time_to?: string | null;
}

export interface AvailabilityPolicyValue {
  channels?: string[] | null;
  schedule?: AvailabilitySchedule | null;
  min_quantity?: number | null;
  max_quantity?: number | null;
  max_discount_percent?: number | null;
  max_bonus_spend_percent?: number | null;
  max_bonus_earn_percent?: number | null;
}

const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Валидация только блока schedule — переиспользуется PriceListsPage (там нет
 * остальных полей AvailabilityPolicy, только branch-scoped schedule). */
export function validateSchedule(sched: AvailabilitySchedule | null | undefined): string | null {
  if (!sched) return null;
  const hasFrom = sched.time_from != null && sched.time_from !== '';
  const hasTo = sched.time_to != null && sched.time_to !== '';
  if (hasFrom !== hasTo) {
    return 'Укажите оба времени начала и окончания расписания либо не указывайте ни одного';
  }
  if (hasFrom && sched.time_from === sched.time_to) {
    return 'Время начала и окончания расписания не может совпадать (для доступности на весь день не указывайте время)';
  }
  if (hasFrom && !HHMM_RE.test(sched.time_from as string)) {
    return 'Некорректное время начала расписания';
  }
  if (hasTo && !HHMM_RE.test(sched.time_to as string)) {
    return 'Некорректное время окончания расписания';
  }
  return null;
}

/** Чистая функция валидации полной AvailabilityPolicy — используется и в
 * компоненте, и как Form.Item rule на страницах Product/Category. */
export function validateAvailabilityPolicy(policy: AvailabilityPolicyValue | null | undefined): string | null {
  if (!policy) return null;
  const scheduleErr = validateSchedule(policy.schedule);
  if (scheduleErr) return scheduleErr;
  if (policy.min_quantity != null && (!Number.isFinite(policy.min_quantity) || policy.min_quantity < 1)) {
    return 'Минимальное количество должно быть целым числом не меньше 1';
  }
  if (policy.max_quantity != null && (!Number.isFinite(policy.max_quantity) || policy.max_quantity < 1)) {
    return 'Максимальное количество должно быть целым числом не меньше 1';
  }
  if (policy.min_quantity != null && policy.max_quantity != null && policy.max_quantity < policy.min_quantity) {
    return 'Максимальное количество не может быть меньше минимального';
  }
  for (const [field, label] of [
    ['max_discount_percent', 'максимальной скидки'],
    ['max_bonus_spend_percent', 'максимального списания бонусов'],
    ['max_bonus_earn_percent', 'максимального начисления бонусов'],
  ] as const) {
    const v = (policy as any)[field];
    if (v != null && (!Number.isFinite(v) || v < 0 || v > 100)) {
      return `Процент ${label} должен быть от 0 до 100`;
    }
  }
  return null;
}
